import React, { useEffect, useRef, useState } from "react";
import { LifeBuoy, Send, X, Minus } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";

/**
 * Floating support chat widget available to every logged-in user (owner + rep).
 * Users message the platform support team; super_admin replies from admin panel.
 */
export default function SupportWidget() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const L = lang === "el";
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [unread, setUnread] = useState(0);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const enabled = user && (user.role === "company_owner" || user.role === "sales_rep");

  const loadMessages = async () => {
    try {
      const { data } = await api.get("/support/messages");
      setMessages(data || []);
    } catch { /* ignore */ }
  };

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/support/unread-count");
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  };

  // Poll unread badge every 20s
  useEffect(() => {
    if (!enabled) return;
    loadUnread();
    const id = setInterval(loadUnread, 20000);
    return () => clearInterval(id);
  }, [enabled]);

  // When opened, load thread + reset unread
  useEffect(() => {
    if (!enabled || !open) return;
    loadMessages().then(() => setUnread(0));
    const id = setInterval(loadMessages, 8000); // live polling while open
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, enabled]);

  // Auto-scroll bottom on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  if (!enabled) return null;

  const send = async () => {
    const b = body.trim();
    if (!b || sending) return;
    setSending(true);
    try {
      await api.post("/support/messages", { body: b });
      setBody("");
      await loadMessages();
    } catch { /* silent */ }
    finally { setSending(false); }
  };

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(L ? "el-GR" : "en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  // Floating launcher
  if (!open) {
    return (
      <button
        data-testid="support-widget-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 left-6 w-14 h-14 rounded-full bg-slate-900 hover:bg-slate-800 text-white shadow-lg flex items-center justify-center z-40 group"
        title={L ? "Υποστήριξη" : "Support"}
      >
        <LifeBuoy className="w-6 h-6" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white" data-testid="support-widget-unread">
            {unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 left-6 w-96 max-w-[calc(100vw-3rem)] h-[520px] bg-white rounded-xl shadow-2xl border border-slate-200 z-40 flex flex-col overflow-hidden" data-testid="support-widget-panel">
      <header className="px-4 py-3 bg-slate-900 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LifeBuoy className="w-4 h-4" />
          <div>
            <div className="text-sm font-semibold">{L ? "Υποστήριξη" : "Support"}</div>
            <div className="text-[10px] text-slate-300">{L ? "Απαντάμε εντός λίγων ωρών" : "We reply within hours"}</div>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded" data-testid="support-widget-close">
          <X className="w-4 h-4" />
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50/50" data-testid="support-widget-messages">
        {messages.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-8">
            <LifeBuoy className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <div className="font-medium text-slate-700">{L ? "Πώς μπορούμε να βοηθήσουμε;" : "How can we help?"}</div>
            <div className="text-xs mt-1 px-4">{L ? "Στείλτε μας ό,τι θέλετε — τεχνικό πρόβλημα, ερώτηση χρέωσης, οτιδήποτε." : "Send us anything — technical issue, billing, feedback."}</div>
          </div>
        )}
        {messages.map((m) => {
          const mine = m.from_role === "user";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${mine ? "bg-[#0052CC] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                {!mine && <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-0.5">{L ? "Ομάδα υποστήριξης" : "Support team"}</div>}
                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-slate-400"}`}>{fmt(m.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-slate-200 p-3 flex items-end gap-2 bg-white">
        <textarea
          data-testid="support-widget-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={2}
          placeholder={L ? "Γράψτε το μήνυμά σας..." : "Write your message..."}
          className="flex-1 resize-none px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/30"
        />
        <button
          data-testid="support-widget-send"
          onClick={send}
          disabled={!body.trim() || sending}
          className="w-10 h-10 rounded-md bg-[#0052CC] hover:bg-[#0043A8] text-white flex items-center justify-center shadow-sm disabled:opacity-40"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
